import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { NumberFormatter } from '@repo/toolkit/number'
import { toFullName } from '@repo/toolkit/to-full-name'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { ArrowLeftRight, CoinIcon, CoinThreeIcon, InformationIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'

import { API } from '../../api/apis.config'
import { generateAdvanceCenterOlPayments } from '../../api/center-ol-payments/generate-advance-center-ol-payments'
import { generateCostCenterOlPayments } from '../../api/center-ol-payments/generate-cost-center-ol-payments'
import { generateFinalCostCenterOlPayments } from '../../api/center-ol-payments/generate-final-cost-center-ol-payments'
import { getContactInParis } from '../../api/ols/gte-contact-in-paris'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import InformationToolTip from '../../components/elements/information-tooltip'
import UserNavBar from '../../components/elements/nav-bar'
import PeriodBadge from '../../components/elements/period-badge'
import { DisplayPayments } from '../../components/pages/center-ol-payments/utils'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import useTranslate from '../../lib/hooks/use-translate'
import { CenterStatus, centerStatusMapping } from '../../lib/types/enums/center/center-status'
import { CenterOlPaymentTypeEnum } from '../../lib/types/enums/center-ol-payment/center-ol-payment-type.enum'
import { CurrencyEnum } from '../../lib/types/enums/common/currency.enum'
import { OLTypeEnum } from '../../lib/types/enums/ol/ol-type.enum'
import { CenterPayment } from '../../lib/types/models/center/center'
import { CenterFilter } from '../../lib/types/models/center/center-filter'
import { CenterProperties } from '../../lib/types/models/center/properties.center'
import {
  transformCentersFiltersToUrl,
  transformUrlToCenterFilters
} from '../../lib/utils/filters-mapping/centers'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import { getTranslatedName } from '../../lib/utils/functions/translation.functions'
import { CenterPaymentsFilter } from './center-payments-filter'

const centerPaymentInitialFilters: CenterFilter = {
  olTypes: [OLTypeEnum.LEC]
}

export function CenterPaymentsListPage() {
  const { t } = useTranslate()
  const tableRef = React.useRef<DynamicTableRef<CenterPayment>>(null)
  const [isTableLoading, setIsTableLoading] = useState(false)

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<CenterFilter>({
      initialFilters: centerPaymentInitialFilters,
      options: { allowSearchParam: true },
      transformFilterToUrl: transformCentersFiltersToUrl,
      transformUrlToFilter: transformUrlToCenterFilters
    })

  const { modalState, toggleModal, closeModel } = useModalManager<'filters'>(['filters'])

  const { mutateAsync: generateAdvancePayments } = useMutation({
    onError: showAxiosError,
    mutationKey: ['generate-advance-center-ol-payment'],
    mutationFn: generateAdvanceCenterOlPayments,
    onSuccess: () => toast.success('Les virements ont été créés avec succès')
  })

  const { mutateAsync: generateCostPayments } = useMutation({
    onError: showAxiosError,
    mutationKey: ['generate-cost-center-ol-payment'],
    mutationFn: generateCostCenterOlPayments,
    onSuccess: () => toast.success('Les virements ont été créés avec succès')
  })

  const { mutateAsync: generateFinalCostPayments } = useMutation({
    onError: showAxiosError,
    mutationKey: ['generate-final-cost-center-ol-payment'],
    mutationFn: generateFinalCostCenterOlPayments,
    onSuccess: () => toast.success('Les virements ont été créés avec succès')
  })

  const { data: users } = useQuery({
    queryKey: ['users', 'admins'],
    queryFn: getContactInParis
  })

  const columns: Column<CenterPayment>[] = [
    {
      accessorKey: 'periods',
      header: 'Périodes',
      cell: ({ row }) => {
        // remove duplicates periods
        const periods = Array.from(
          new Map(
            row.original.centerToJourneys
              .map((ctj) => ctj?.journey?.date?.period)
              .filter(Boolean)
              .map((period) => [period.id, period])
          ).values()
        )

        if (periods.length === 0) return <div className="text-center">-</div>
        return (
          <div className={'flex flex-wrap  py-3 gap-1'}>
            {periods.map((period, index) => (
              <PeriodBadge key={index} period={period} />
            ))}
          </div>
        )
      }
    },
    {
      accessorKey: 'name',
      header: 'Centre',
      cell: ({ row }) => (
        <Link to={`/ols/centers/details/${row.original.id}/placements`}>
          <Typography.Link variant="secondary-body">{row.original.name}</Typography.Link>
        </Link>
      )
    },
    {
      accessorKey: 'contactParis',
      header: 'Contact Paris',
      cell: ({ row }) => {
        const contactInParisEmail = row.original?.ol?.contactInParis?.email
        const userContactInParis = users?.data.find((u) => u.email === contactInParisEmail)

        if (!contactInParisEmail || !userContactInParis) return <div>-</div>
        return (
          <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
            {toFullName(userContactInParis.firstName, userContactInParis.lastName)}
          </Typography>
        )
      }
    },
    {
      accessorKey: 'formula',
      header: 'Formule',
      cell: ({ row }) => {
        // remove duplicates formulas
        const formulas = Array.from(
          new Map(
            row.original.centerToJourneys
              .map((ctj) => ctj?.journey?.date?.product.formula)
              .filter(Boolean)
              .map((formula) => [formula.id, formula])
          ).values()
        )

        if (formulas.length === 0) return <div>-</div>
        return (
          <div className={'flex flex-col py-2 gap-1'}>
            {formulas.map((formula, index) => (
              <Typography key={index} variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
                {getTranslatedName(formula)}
              </Typography>
            ))}
          </div>
        )
      }
    },
    {
      accessorKey: 'viewData.affectedStudentsNumber',
      header: '# de jeunes',
      cell: ({ row }) => {
        const { costsStatus, fixedNumberOfEnrollments, viewData } = row.original
        return (
          <Typography
            variant="secondary-body"
            className="text-center"
            color={tailwindColors.grey.DEFAULT}
          >
            {costsStatus === CenterStatus.VALIDATED
              ? fixedNumberOfEnrollments
              : viewData.affectedStudentsNumber}
          </Typography>
        )
      }
    },
    {
      accessorKey: 'stayDates',
      header: 'Dates du séjour',
      cell: ({ row }) => {
        if (!CenterProperties.getCenterDates(row.original))
          return <div className="text-center">-</div>

        return (
          <Badge
            className="whitespace-nowrap h-fit py-2"
            text={CenterProperties.getCenterDates(row.original)}
          />
        )
      }
    },
    {
      accessorKey: 'nightsCount',
      header: '# de nuits',
      cell: ({ row }) => {
        const nightsCount = CenterProperties.centerNightsCount(row.original)
        if (!nightsCount) return <div className="text-center">-</div>
        return (
          <Typography
            variant="secondary-body"
            className="text-center"
            color={tailwindColors.grey.DEFAULT}
          >
            {nightsCount}
          </Typography>
        )
      }
    },
    {
      accessorKey: 'costOfCenter',
      header: 'Cost of center',
      cell: ({ row }) => (
        <InformationToolTip
          contentVariant="secondary"
          icon={
            <Badge
              text={
                <CoinIcon
                  filled
                  color={centerStatusMapping[row.original.costsStatus].color}
                  color2={centerStatusMapping[row.original.costsStatus].background}
                />
              }
              color={centerStatusMapping[row.original.costsStatus].background}
              className="h-8"
            />
          }
        >
          <Typography
            variant="label-medium"
            color={tailwindColors.black.DEFAULT}
            className="text-pretty"
          >
            {t(centerStatusMapping[row.original.costsStatus].activityLabel)}
          </Typography>
        </InformationToolTip>
      )
    },
    {
      accessorKey: 'finalCost',
      header: 'Final Cost',
      cell: ({ row }) => (
        <InformationToolTip
          contentVariant="secondary"
          icon={
            <Badge
              text={
                <CoinThreeIcon color={centerStatusMapping[row.original.finalCostsStatus].color} />
              }
              color={centerStatusMapping[row.original.finalCostsStatus].background}
              className="h-8"
            />
          }
        >
          <Typography
            variant="label-medium"
            color={tailwindColors.black.DEFAULT}
            className="text-pretty"
          >
            {t(centerStatusMapping[row.original.finalCostsStatus].activityLabel)}
          </Typography>
        </InformationToolTip>
      )
    },
    {
      accessorKey: 'totalCosts.totalCost',
      header: 'Total Cost of Center',
      centerCell: true,
      cell: ({ row }) => {
        const totalCost = row.original.totalCosts.totalCost
        const currency = row.original.ol?.currency ?? CurrencyEnum.EUR

        if (totalCost === undefined) return <div>-</div>
        return (
          <Badge text={NumberFormatter.formatCurrency(totalCost, currency)} variant="primary" />
        )
      }
    },
    {
      accessorKey: 'costPerYoungCostOfCenter',
      header: 'Coût / jeune / cost of center',
      centerCell: true,
      headerInfo: (
        <InformationToolTip
          contentVariant="secondary"
          icon={
            <InformationIcon width="20px" height="20px" color={tailwindColors.secondary.DEFAULT} />
          }
          size="small"
          contentClassName="p-2"
        >
          <Typography variant={'label-medium'} color={tailwindColors.black.DEFAULT}>
            Hors miscellaneous
          </Typography>
        </InformationToolTip>
      ),
      cell: ({ row }) => {
        const { totalCosts, costsStatus, fixedNumberOfEnrollments, viewData } = row.original
        const currency = row.original.ol?.currency ?? CurrencyEnum.EUR
        if (
          !totalCosts.totalCost ||
          (costsStatus === CenterStatus.VALIDATED
            ? !fixedNumberOfEnrollments
            : !viewData.affectedStudentsNumber)
        )
          return <div>-</div>
        return (
          <Badge
            text={NumberFormatter.formatCurrency(
              Number(
                (
                  totalCosts.totalCost /
                  (costsStatus === CenterStatus.VALIDATED
                    ? fixedNumberOfEnrollments
                    : viewData.affectedStudentsNumber)
                ).toFixed(2)
              ),
              currency
            )}
            variant="primary"
          />
        )
      }
    },
    {
      accessorKey: 'fixedFinalNumberOfEnrollments',
      header: '# final de jeunes',
      cell: ({ row }) => {
        const { finalCostsStatus, fixedFinalNumberOfEnrollments, viewData } = row.original
        return (
          <Typography
            variant="secondary-body"
            className="text-center"
            color={tailwindColors.grey.DEFAULT}
          >
            {finalCostsStatus === CenterStatus.VALIDATED
              ? fixedFinalNumberOfEnrollments
              : viewData.affectedStudentsNumber}
          </Typography>
        )
      }
    },
    {
      accessorKey: 'totalCosts.finalTotalCost',
      header: 'Total Final Cost of Center',
      centerCell: true,
      cell: ({ row }) => {
        const totalCost = row.original.totalCosts.finalTotalCost
        const currency = row.original.ol?.currency ?? CurrencyEnum.EUR

        if (totalCost === undefined) return <div>-</div>
        return (
          <Badge text={NumberFormatter.formatCurrency(totalCost, currency)} variant="primary" />
        )
      }
    },
    {
      accessorKey: 'costPerYoungFinal',
      header: 'Coût / jeune final',
      centerCell: true,
      headerInfo: (
        <InformationToolTip
          contentVariant="secondary"
          icon={
            <InformationIcon width="20px" height="20px" color={tailwindColors.secondary.DEFAULT} />
          }
          size="small"
          contentClassName="p-2"
        >
          <Typography variant={'label-medium'} color={tailwindColors.black.DEFAULT}>
            Hors miscellaneous
          </Typography>
        </InformationToolTip>
      ),
      cell: ({ row }) => {
        const { totalCosts, finalCostsStatus, fixedFinalNumberOfEnrollments, viewData } =
          row.original
        const currency = row.original.ol?.currency ?? CurrencyEnum.EUR
        if (
          !totalCosts.finalTotalCost ||
          (finalCostsStatus === CenterStatus.VALIDATED
            ? !fixedFinalNumberOfEnrollments
            : !viewData.affectedStudentsNumber)
        )
          return <div>-</div>
        return (
          <Badge
            text={NumberFormatter.formatCurrency(
              Number(
                (
                  totalCosts.finalTotalCost /
                  (finalCostsStatus === CenterStatus.VALIDATED
                    ? fixedFinalNumberOfEnrollments
                    : viewData.affectedStudentsNumber)
                ).toFixed(2)
              ),
              currency
            )}
            variant="primary"
          />
        )
      }
    },
    {
      accessorKey: 'virementAvance',
      header: 'Virement avance',
      cell: ({ row }) => (
        <DisplayPayments
          type={CenterOlPaymentTypeEnum.ADVANCE}
          payments={row.original.centerBankPayments}
        />
      )
    },
    {
      accessorKey: 'virementBeforeStay',
      header: 'Virement avant séjour',
      headerInfo: (
        <InformationToolTip
          contentVariant="secondary"
          icon={
            <InformationIcon width="20px" height="20px" color={tailwindColors.secondary.DEFAULT} />
          }
          size="small"
          contentClassName="p-2"
        >
          <Typography variant={'label-medium'} color={tailwindColors.black.DEFAULT}>
            Balance du cost
          </Typography>
        </InformationToolTip>
      ),
      cell: ({ row }) => (
        <DisplayPayments
          type={CenterOlPaymentTypeEnum.COST}
          payments={row.original.centerBankPayments}
        />
      )
    },
    {
      accessorKey: 'balanceFinalCost',
      header: 'Balance final cost',
      cell: ({ row }) => (
        <DisplayPayments
          type={CenterOlPaymentTypeEnum.FINAL}
          payments={row.original.centerBankPayments}
        />
      )
    },
    {
      accessorKey: 'amountToPay',
      header: 'Solde à payer',
      cell: ({ row }) => {
        const currency = row.original.ol?.currency ?? CurrencyEnum.EUR

        return (
          <Badge
            variant="primary"
            text={NumberFormatter.formatCurrency(row.original.remainingBalance ?? 0, currency)}
          />
        )
      }
    }
  ]

  return (
    <div>
      <UserNavBar title="Ecran PRESTA LEC de suivi et création virements" icon={ArrowLeftRight} />
      <div className={'h-4'} />

      <DynamicDataTable<CenterPayment, CenterFilter, undefined, 'CENTER_PAYMENT'>
        ref={tableRef}
        version="CENTER_PAYMENT"
        onLoadingChange={setIsTableLoading}
        title="Tous les centres PRESTA LEC"
        displayOrder
        columns={columns}
        selectable
        permissions={{ delete: false }}
        allowSearchParam
        endpoint={API.centers.getAll.url}
        sorting={{ id: 'startDate', desc: false }}
        onFiltersClick={() => toggleModal('filters')}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter)}
        actions={[
          {
            type: 'multiple',
            refetch: true,
            variant: 'ghost',
            label: 'Virement avance',
            fn: async (data) => await generateAdvancePayments(data.map((d) => d.id))
          },
          {
            type: 'multiple',
            refetch: true,
            variant: 'ghost',
            label: 'Virement balance Cost',
            fn: async (data) => await generateCostPayments(data.map((d) => d.id))
          },
          {
            type: 'multiple',
            refetch: true,
            variant: 'ghost',
            label: 'Virement balance Final',
            fn: async (data) => await generateFinalCostPayments(data.map((d) => d.id))
          }
        ]}
      />

      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <CenterPaymentsFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>
    </div>
  )
}
